import React, { useState } from 'react';
import { Body, Tag, Tooltip } from '@panwds/react-ui';

/**
 * Single source of truth for rendering a list of values as PANWDS `Tag` chips with
 * collapse/expand past `collapsedLimit`. Generic over what the values mean --
 * `RegionChipList` wraps this for the `city-*` region values, and
 * `EventSubscriptionsSection` wraps this for subscribed incident/maintenance/advisory
 * IDs -- so both stay visually and behaviorally identical without duplicating the
 * chip/collapse/remove logic.
 *
 * `onRemove` is optional: when omitted, chips render without a close button (used by
 * read-only contexts like `EventSubscriptionsSection`). When provided, it is called
 * with the raw value for the removed chip.
 *
 * `onItemClick` is optional and mutually exclusive with `onRemove` -- PANWDS `Tag`
 * itself disallows combining `onClick` with `closeButtonProps`. When provided, the
 * chip becomes a clickable button (used by `EventSubscriptionsSection` to navigate to
 * an event's detail page) instead of a static tag.
 *
 * `getTooltipLabel` is optional: when provided (alongside `onItemClick`), each chip is
 * wrapped in a `Tooltip` describing what the click does, matching the
 * `<Tooltip><Button/></Tooltip>` pattern in `EventWatchButton`.
 *
 * `emptyLabel` is optional: when provided and `items` is empty, renders an italic
 * secondary message instead of nothing. Omit it when the caller already gates
 * rendering on a non-empty list.
 */
const ChipList = ({
    items,
    isExpanded,
    onToggleExpand,
    onRemove,
    onItemClick,
    getTooltipLabel,
    testIdPrefix,
    collapsedLimit = 10,
    formatLabel = (value) => value,
    emptyLabel,
}) => {
    // Which chip's close button is currently hovered/focused, so its `x` can go red to
    // signal removal -- matching the trash button on the product panel.
    //
    // Driven by state + an inline `style` rather than a CSS `:hover` rule because PANWDS
    // `Tag` renders its own close button and applies `className` *after* spreading
    // `closeButtonProps` (and types that prop as `Omit<..., 'className'>`), so the button's
    // classes cannot be extended. `style` and the event handlers *do* pass through, and an
    // inline style outranks Tag's own classes without touching its internal DOM or needing
    // `!important`. Tag's `CloseIcon` is `twds-text-inherit`, so colouring the button
    // colours the glyph.
    const [hoveredCloseValue, setHoveredCloseValue] = useState(null);

    if (!items || items.length === 0) {
        if (!emptyLabel) return null;
        return <Body size="sm" as="span" appearance="secondary" addClassName="ssmutw-italic">{emptyLabel}</Body>;
    }

    const visibleItems = isExpanded ? items : items.slice(0, collapsedLimit);
    const remainingCount = items.length - collapsedLimit;

    return (
        <>
            {visibleItems.map((value) => {
                const tag = (
                    <Tag
                        dataTestId={`${testIdPrefix}-${value}`}
                        onClick={onItemClick ? () => onItemClick(value) : undefined}
                        closeButtonProps={onRemove ? {
                            // Tag always applies its own i18n'd "Remove" aria-label after
                            // spreading these props, so a custom aria-label here would be
                            // silently dropped -- only the click handler has any effect.
                            onClick: () => onRemove(value),
                            onMouseEnter: () => setHoveredCloseValue(value),
                            onMouseLeave: () => setHoveredCloseValue(null),
                            onFocus: () => setHoveredCloseValue(value),
                            onBlur: () => setHoveredCloseValue(null),
                            style: hoveredCloseValue === value
                                ? { color: 'var(--ssmu-icon-error)' }
                                : undefined,
                        } : undefined}
                    >
                        {formatLabel(value)}
                    </Tag>
                );

                if (!onItemClick || !getTooltipLabel) {
                    return <React.Fragment key={value}>{tag}</React.Fragment>;
                }

                return (
                    <Tooltip key={value} label={getTooltipLabel(value)}>
                        {/*
                         * PANWDS `Tag` is a plain function component (not
                         * `forwardRef`, unlike `Button`) and doesn't forward
                         * arbitrary props to its root element, so Tooltip's
                         * cloned ref/hover handlers would silently no-op if
                         * attached directly to it. Wrapping in a `span` (a
                         * pattern already used elsewhere, e.g.
                         * GlobalProductHeader's constraint-hint tooltips) gives
                         * Tooltip a real DOM node to attach to instead.
                         */}
                        <span>{tag}</span>
                    </Tooltip>
                );
            })}
            {items.length > collapsedLimit && (
                <button
                    type="button"
                    className='ssmutw-mx-2 ssmutw-cursor-pointer ssmutw-inline-flex ssmutw-items-center ssmutw-min-h-[44px] ssmutw-px-2 ssmutw-bg-transparent ssmutw-border-0'
                    style={{ color: 'var(--ssmu-text-link)', textDecoration: 'underline' }}
                    onClick={onToggleExpand}
                    aria-expanded={isExpanded}
                >
                    {isExpanded ? 'Show less' : `+${remainingCount} more`}
                </button>
            )}
        </>
    );
};

export default React.memo(ChipList);

